import { hasNativeId, normalizeContactPhone } from './policy';

export class ContactsError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

const assertCurrent = isCurrent => {
  if (!isCurrent()) throw new ContactsError('unavailable');
};

// Bind every native request to the captured account, rather than ApiClient's
// mutable window.location account. Native Rails permissions remain authoritative.
export const nativeContactsApi = (scope, http) => {
  const base = `/api/v1/accounts/${scope.accountId}`;
  return {
    lookup: async phone => {
      const { data } = await http.get(`${base}/contacts/search`, {
        params: { q: phone, include_contact_inboxes: false },
      });
      return data;
    },
    createContact: async payload => {
      const { data } = await http.post(`${base}/contacts`, payload);
      return data?.payload?.contact;
    },
    history: async contactId => {
      const { data } = await http.get(
        `${base}/contacts/${contactId}/conversations`
      );
      return data?.payload;
    },
    bindings: async contactId => {
      const { data } = await http.get(
        `${base}/contacts/${contactId}/contactable_inboxes`
      );
      return data?.payload;
    },
    snapshot: async contactId => {
      const { data } = await http.get(`${base}/contacts/${contactId}`, {
        params: { include_contact_inboxes: true },
      });
      return data?.payload;
    },
    owner: async phone => {
      try {
        // This native filter is a read-only inbox-authorized POST. It resolves
        // a persisted source ID's real owner, rather than a computed capability.
        const { data } = await http.post(`${base}/contact_inboxes/filter`, {
          inbox_id: scope.inboxId,
          source_id: phone.slice(1),
        });
        return data;
      } catch (error) {
        if (error.response?.status === 404) return null;
        throw error;
      }
    },
    createConversation: async contactId => {
      const { data } = await http.post(`${base}/conversations`, {
        inbox_id: scope.inboxId,
        contact_id: contactId,
      });
      return data;
    },
  };
};

const exactContact = (response, phone) => {
  if (!Array.isArray(response?.payload))
    throw new ContactsError('lookup_failed');
  if (response.meta?.has_more) throw new ContactsError('lookup_incomplete');
  const matches = response.payload.filter(
    contact => normalizeContactPhone(contact.phone_number) === phone
  );
  if (matches.length > 1) throw new ContactsError('lookup_incomplete');
  const contact = matches[0];
  if (contact && !hasNativeId(contact.id))
    throw new ContactsError('response_mismatch');
  return contact || null;
};

export const prepareContact = async ({
  api,
  phone,
  name,
  isCurrent,
  existingOnly = false,
}) => {
  if (!normalizeContactPhone(phone)) throw new ContactsError('invalid_phone');
  if (name.trim().length > 255) throw new ContactsError('invalid_name');
  assertCurrent(isCurrent);
  let response;
  try {
    response = await api.lookup(phone);
  } catch {
    throw new ContactsError('lookup_failed');
  }
  assertCurrent(isCurrent);
  const existing = exactContact(response, phone);
  let owner;
  try {
    owner = await api.owner(phone);
  } catch {
    throw new ContactsError('lookup_failed');
  }
  assertCurrent(isCurrent);
  if (
    owner &&
    (!hasNativeId(owner.id) ||
      normalizeContactPhone(owner.phone_number) !== phone ||
      (existing && Number(owner.id) !== Number(existing.id)))
  ) {
    throw new ContactsError('binding_mismatch');
  }
  if (existing || owner) return { contact: existing || owner, reused: true };
  if (existingOnly) throw new ContactsError('contact_unknown');

  try {
    // Save only contact data. Do not manufacture an incoming message, send a
    // template, or alter existing channel identity/assignment/history here.
    const contact = await api.createContact({
      name: name.trim() || phone,
      phone_number: phone,
    });
    assertCurrent(isCurrent);
    if (
      !hasNativeId(contact?.id) ||
      normalizeContactPhone(contact.phone_number) !== phone
    ) {
      throw new ContactsError('contact_unknown');
    }
    return { contact, reused: false };
  } catch (error) {
    assertCurrent(isCurrent);
    // A timed-out POST may have committed. Only a positive native readback
    // permits continuation; never repeat this POST in the same attempt.
    try {
      const contact = exactContact(await api.lookup(phone), phone);
      assertCurrent(isCurrent);
      if (contact) return { contact, reused: true };
    } catch (readError) {
      if (readError.code === 'unavailable') throw readError;
    }
    throw new ContactsError('contact_unknown');
  }
};

const existingConversation = (rows, scope, contact) => {
  if (!Array.isArray(rows)) throw new ContactsError('history_failed');
  const matches = rows.filter(row => Number(row.inbox_id) === scope.inboxId);
  matches.forEach(row => {
    if (
      Number(row.account_id) !== scope.accountId ||
      Number(row.meta?.sender?.id) !== Number(contact.id) ||
      normalizeContactPhone(row.meta?.sender?.phone_number) !==
        normalizeContactPhone(contact.phone_number) ||
      !hasNativeId(row.id)
    ) {
      throw new ContactsError('response_mismatch');
    }
  });
  // The native history endpoint returns at most 20 rows. Avoid creating a
  // duplicate when a matching older conversation may be outside that window.
  if (!matches.length && rows.length >= 20)
    throw new ContactsError('lookup_incomplete');
  return matches[0] || null;
};

const validateStoredBinding = (
  snapshot,
  owner,
  scope,
  contact,
  allowMissing
) => {
  const phone = normalizeContactPhone(contact.phone_number);
  if (
    Number(snapshot?.id) !== Number(contact.id) ||
    normalizeContactPhone(snapshot?.phone_number) !== phone ||
    !Array.isArray(snapshot?.contact_inboxes)
  )
    throw new ContactsError('binding_mismatch');
  const stored = snapshot.contact_inboxes.filter(
    binding => Number(binding.inbox?.id) === scope.inboxId
  );
  if (!stored.length && !owner && allowMissing) return;
  if (
    stored.length !== 1 ||
    stored[0].source_id !== phone.slice(1) ||
    Number(owner?.id) !== Number(contact.id) ||
    normalizeContactPhone(owner?.phone_number) !== phone
  )
    throw new ContactsError('binding_mismatch');
};

export const prepareConversation = async ({
  api,
  scope,
  contact,
  isCurrent,
  existingOnly = false,
}) => {
  assertCurrent(isCurrent);
  let rows;
  let bindings;
  let snapshot;
  let owner;
  try {
    rows = await api.history(contact.id);
    assertCurrent(isCurrent);
    bindings = await api.bindings(contact.id);
    assertCurrent(isCurrent);
    snapshot = await api.snapshot(contact.id);
    assertCurrent(isCurrent);
    owner = await api.owner(normalizeContactPhone(contact.phone_number));
  } catch (error) {
    if (error.code === 'unavailable') throw error;
    throw new ContactsError('history_failed');
  }
  assertCurrent(isCurrent);
  if (!Array.isArray(bindings)) throw new ContactsError('response_mismatch');
  const matching = bindings.filter(
    binding => Number(binding.inbox?.id) === scope.inboxId
  );
  if (!matching.length) throw new ContactsError('unavailable');
  if (
    matching.length > 1 ||
    matching.some(
      binding =>
        binding.source_id !==
          normalizeContactPhone(contact.phone_number)?.slice(1) ||
        binding.inbox.channel_type !== 'Channel::Whatsapp' ||
        binding.inbox.provider !== 'whatsapp_cloud'
    )
  ) {
    throw new ContactsError('binding_mismatch');
  }
  const existing = existingConversation(rows, scope, contact);
  validateStoredBinding(snapshot, owner, scope, contact, !existing);
  if (existing) return existing;
  if (existingOnly) throw new ContactsError('conversation_unknown');

  try {
    const created = await api.createConversation(contact.id);
    assertCurrent(isCurrent);
    const validated = existingConversation([created], scope, contact);
    if (!validated) throw new ContactsError('response_mismatch');
    const after = await api.snapshot(contact.id);
    assertCurrent(isCurrent);
    const afterOwner = await api.owner(
      normalizeContactPhone(contact.phone_number)
    );
    assertCurrent(isCurrent);
    validateStoredBinding(after, afterOwner, scope, contact, false);
    return validated;
  } catch {
    assertCurrent(isCurrent);
    try {
      const existingAfterPost = existingConversation(
        await api.history(contact.id),
        scope,
        contact
      );
      assertCurrent(isCurrent);
      if (existingAfterPost) {
        const after = await api.snapshot(contact.id);
        assertCurrent(isCurrent);
        const afterOwner = await api.owner(
          normalizeContactPhone(contact.phone_number)
        );
        assertCurrent(isCurrent);
        validateStoredBinding(after, afterOwner, scope, contact, false);
        return existingAfterPost;
      }
    } catch (readError) {
      if (readError.code === 'unavailable') throw readError;
    }
    throw new ContactsError('conversation_unknown');
  }
};
